import { DateHelper } from "utils/helpers";
import { AuthEnums } from "models/enums";
import {
  WintSuperkollAPIModelExportItems,
  WintSuperkollAPIModelFinancialReport,
  WintUtilsWebApi,
} from "wint-api-client/dist/types";
import useAPIClientHelper from "./useAPIClientHelper";
import { ExportItemsService } from "services";
import { AccountingFormTypes } from "models/formTypes";

function useFinancialReportService() {
  const { apiClientInstance } = useAPIClientHelper();

  const getResultReport = (filter: WintSuperkollAPIModelFinancialReport.FinancialReportFilter) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .FinancialReportsClient.GetResultReport(filter)
      .then((res) => res.data);
  };

  const getMonthlyResultReport = (filter: WintSuperkollAPIModelFinancialReport.FinancialReportMonthlyFilter) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .FinancialReportsClient.GetMonthlyResultReport(filter)
      .then((res) => res.data);
  };

  const getDimensionResultReport = (
    filter: WintSuperkollAPIModelFinancialReport.FinancialReportMultiDimensionFilter
  ) => {
    return filter.crossDimensionTypes?.length
      ? apiClientInstance(AuthEnums.AuthType.Company)
          .FinancialReportsClient.GetMultiDimensionReport(filter)
          .then((res) => res.data)
      : apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true })
          .FinancialReportsClient.GetDimensionReport(filter)
          .then((res) => res.data);
  };

  const getBalanceReport = (filter: WintSuperkollAPIModelFinancialReport.FinancialReportFilter) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .FinancialReportsClient.GetBalanceReport(filter)
      .then((res) => res.data);
  };

  const getMonthlyBalanceReport = (filter: WintSuperkollAPIModelFinancialReport.FinancialReportMonthlyFilter) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .FinancialReportsClient.GetMonthlyBalanceReport(filter)
      .then((res) => res.data);
  };

  const getAuditBalanceReport = (filter: WintSuperkollAPIModelFinancialReport.AuditReportFilter) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .FinancialReportsClient.GetAuditBalanceReport(filter)
      .then((res) => res.data);
  };

  const getAuditBalanceReportFilterData = () => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .FinancialReportsClient.GetAuditBalanceReportFilterData()
      .then((res) => res.data);
  };

  const getWidgetReconciliationReport = (reconciliationDate: Date) => {
    const filter: WintSuperkollAPIModelFinancialReport.AuditReportFilter = {
      widgetDataOnly: true,
      includeZeroValueRows: false,
      endMonth: DateHelper.getYearMonthByDate(reconciliationDate),
    };

    return getAuditBalanceReport(filter);
  };

  // expandAllRows is a presentational-only flag that "save as default" persists in the saved filter's FilterData.
  // Core validates FilterData against its typed filter models and rejects unknown fields, so the field must exist
  // there too (Core#14253). The generated API types don't have it yet, hence the widened return type.
  const standardReportToAPIFilter = (
    pageFilter?: AccountingFormTypes.FinancialReportFilter
  ): Omit<WintSuperkollAPIModelFinancialReport.FinancialReportFilter, keyof WintUtilsWebApi.Filter> &
    Pick<AccountingFormTypes.FinancialReportFilter, "expandAllRows"> => {
    const columns =
      pageFilter?.monthRange?.start && pageFilter?.monthRange?.end
        ? [
            {
              startMonth: DateHelper.getYearMonthByDate(pageFilter?.monthRange?.start),
              endMonth: DateHelper.getYearMonthByDate(pageFilter.monthRange.end),
            },
          ]
        : [];
    return {
      columns: columns,
      dimensions: pageFilter?.dimensions,
      showAccountNumber: pageFilter?.showAccountNumber,
      hideAverageMonthAndDifference: pageFilter?.hideAverageMonthAndDifference,
      expandAllRows: pageFilter?.expandAllRows,
    };
  };

  // Kept apart from standardReportToAPIFilter, which the balance report pie widgets also use and which
  // must not carry the report's comparison columns.
  const standardReportWithComparisonColumnsToAPIFilter = (
    pageFilter?: AccountingFormTypes.FinancialReportFilter
  ): Omit<WintSuperkollAPIModelFinancialReport.FinancialReportFilter, keyof WintUtilsWebApi.Filter> => {
    const apiFilter = standardReportToAPIFilter(pageFilter);
    if (!apiFilter.columns?.length) return apiFilter;

    const comparisonColumns = (pageFilter?.comparisonColumns ?? [])
      .filter((range) => range.start && range.end)
      .map((range) => ({
        startMonth: DateHelper.getYearMonthByDate(range.start!),
        endMonth: DateHelper.getYearMonthByDate(range.end!),
      }));

    return { ...apiFilter, columns: [...apiFilter.columns, ...comparisonColumns] };
  };

  const monthlyReportToAPIFilter = (
    pageFilter?: AccountingFormTypes.FinancialReportFilter
  ): Omit<WintSuperkollAPIModelFinancialReport.FinancialReportMonthlyFilter, keyof WintUtilsWebApi.Filter> &
    Pick<AccountingFormTypes.FinancialReportFilter, "expandAllRows"> => {
    return {
      startMonth: pageFilter?.monthRange?.start
        ? DateHelper.getYearMonthByDate(pageFilter.monthRange.start)
        : undefined,
      endMonth: pageFilter?.monthRange?.end ? DateHelper.getYearMonthByDate(pageFilter.monthRange.end) : undefined,
      dimensions: pageFilter?.dimensions,
      showAccountNumber: pageFilter?.showAccountNumber,
      hideAverageMonthAndDifference: pageFilter?.hideAverageMonthAndDifference,
      expandAllRows: pageFilter?.expandAllRows,
    };
  };

  const dimensionReportToAPIFilter = (
    pageFilter?: AccountingFormTypes.FinancialReportFilter
  ): Omit<WintSuperkollAPIModelFinancialReport.FinancialReportMultiDimensionFilter, keyof WintUtilsWebApi.Filter> &
    Pick<AccountingFormTypes.FinancialReportFilter, "expandAllRows"> => {
    return {
      startMonth: pageFilter?.monthRange?.start
        ? DateHelper.getYearMonthByDate(pageFilter.monthRange.start)
        : undefined,
      endMonth: pageFilter?.monthRange?.end ? DateHelper.getYearMonthByDate(pageFilter.monthRange.end) : undefined,
      dimensionType: pageFilter?.dimensionTypeId,
      crossDimensionTypes: pageFilter?.secondDimensionTypes?.map((sd) => sd.id as string),
      showAccountNumber: pageFilter?.showAccountNumber,
      hideAverageMonthAndDifference: pageFilter?.hideAverageMonthAndDifference,
      expandAllRows: pageFilter?.expandAllRows,
    };
  };

  const reconciliationReportToAPIFilter = (
    pageFilter?: AccountingFormTypes.ReconciliationReportFilter
  ): Omit<WintSuperkollAPIModelFinancialReport.AuditReportFilter, keyof WintUtilsWebApi.Filter> => {
    return {
      reconciliationId: pageFilter?.reconciliationId,
      widgetDataOnly: false,
      includeZeroValueRows: false,
    };
  };

  const queueExportReconciliations = (entityId: number) => {
    return ExportItemsService.queueExport(
      entityId,
      WintSuperkollAPIModelExportItems.ExportItemType.FullAuditSheetPdfWithAttachments
    );
  };

  const getLastReconciliationIdForFinancialYear = (financialYearId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .FinancialReportsClient.GetLastReconciliationIdForFinancialYear(financialYearId)
      .then((res) => res?.data);
  };

  return {
    getResultReport,
    getMonthlyResultReport,
    getDimensionResultReport,
    getBalanceReport,
    getMonthlyBalanceReport,
    getWidgetReconciliationReport,
    standardReportToAPIFilter,
    standardReportWithComparisonColumnsToAPIFilter,
    monthlyReportToAPIFilter,
    dimensionReportToAPIFilter,
    reconciliationReportToAPIFilter,
    queueExportReconciliations,
    getAuditBalanceReport,
    getAuditBalanceReportFilterData,
    getLastReconciliationIdForFinancialYear,
  };
}

export default useFinancialReportService;
